/**
 * App Transaction Remapper
 * Flutter source: lib/data/models/app_transaction.dart (fromJson)
 *
 * Maps raw Firestore document data to AppTransactionDto and AppTransactionEntity.
 * Delegates info-type mapping to dedicated info remappers.
 */
import type { AppTransactionDto } from '@/data/dtos/transaction/appTransactionDto';
import type { JsonObject } from '@/data/types/json';
import type { AppTransactionEntity } from '@/domain/entities/transaction/appTransactionEntity';
import { UsersInfoRemapper } from '@/domain/remappers/info/usersInfoRemapper';
import { WalletInfoRemapper } from '@/domain/remappers/info/walletInfoRemapper';
import { MediaInfoRemapper } from '@/domain/remappers/info/mediaInfoRemapper';
import { BroadcastingInfoRemapper } from '@/domain/remappers/info/broadcastingInfoRemapper';
import { GiftInfoRemapper } from '@/domain/remappers/info/giftInfoRemapper';
import { SubscriptionInfoRemapper } from '@/domain/remappers/info/subscriptionInfoRemapper';
import { ChatInfoRemapper } from '@/domain/remappers/info/chatInfoRemapper';
import { ChatVideoCallInfoRemapper } from '@/domain/remappers/info/chatVideoCallInfoRemapper';
import { PremiumPackageInfoRemapper } from '@/domain/remappers/info/premiumPackageInfoRemapper';
import { RoadmapItemInfoRemapper } from '@/domain/remappers/info/roadmapItemInfoRemapper';

export class AppTransactionRemapper {
  /**
   * Map raw Firestore document data to AppTransactionDto
   */
  static fromJson(data: JsonObject): AppTransactionDto {
    const walletInfo = data['walletInfo'] as JsonObject | undefined;
    const mediaInfo = data['mediaInfo'] as JsonObject | undefined;
    const broadcastingInfo = data['broadcastingInfo'] as JsonObject | undefined;
    const giftInfo = data['giftInfo'] as JsonObject | undefined;
    const subscriptionInfo = data['subscriptionInfo'] as JsonObject | undefined;
    const chatInfo = data['chatInfo'] as JsonObject | undefined;
    const chatVideoCallInfo = data['chatVideoCallInfo'] as JsonObject | undefined;
    const premiumPackageInfo = data['premiumPackageInfo'] as JsonObject | undefined;
    const roadmapItemInfo = data['roadmapItemInfo'] as JsonObject | undefined;
    return {
      usersInfo: UsersInfoRemapper.fromJson((data['usersInfo'] as JsonObject | undefined) ?? {}),
      walletInfo: walletInfo ? WalletInfoRemapper.fromJson(walletInfo) : undefined,
      mediaInfo: mediaInfo ? MediaInfoRemapper.fromJson(mediaInfo) : undefined,
      broadcastingInfo: broadcastingInfo ? BroadcastingInfoRemapper.fromJson(broadcastingInfo) : undefined,
      giftInfo: giftInfo ? GiftInfoRemapper.fromJson(giftInfo) : undefined,
      subscriptionInfo: subscriptionInfo ? SubscriptionInfoRemapper.fromJson(subscriptionInfo) : undefined,
      chatInfo: chatInfo ? ChatInfoRemapper.fromJson(chatInfo) : undefined,
      chatVideoCallInfo: chatVideoCallInfo ? ChatVideoCallInfoRemapper.fromJson(chatVideoCallInfo) : undefined,
      premiumPackageInfo: premiumPackageInfo ? PremiumPackageInfoRemapper.fromJson(premiumPackageInfo) : undefined,
      roadmapItemInfo: roadmapItemInfo ? RoadmapItemInfoRemapper.fromJson(roadmapItemInfo) : undefined,
      transactionType: (data['transactionType'] as number | undefined) ?? 0,
      createdDate: (data['createdDate'] as number | undefined) ?? 0,
      updatedDate: data['updatedDate'] as number | undefined,
      expireDate: data['expireDate'] as number | undefined,
      name: data['name'] as string | undefined,
      description: data['description'] as string | undefined,
    };
  }

  /**
   * Convert DTO to Entity
   */
  static toEntity(dto: AppTransactionDto): AppTransactionEntity {
    return {
      usersInfo: UsersInfoRemapper.fromDto(dto.usersInfo),
      walletInfo: dto.walletInfo ? WalletInfoRemapper.fromDto(dto.walletInfo) : undefined,
      mediaInfo: dto.mediaInfo ? MediaInfoRemapper.fromDto(dto.mediaInfo) : undefined,
      broadcastingInfo: dto.broadcastingInfo ? BroadcastingInfoRemapper.fromDto(dto.broadcastingInfo) : undefined,
      giftInfo: dto.giftInfo ? GiftInfoRemapper.fromDto(dto.giftInfo) : undefined,
      subscriptionInfo: dto.subscriptionInfo ? SubscriptionInfoRemapper.fromDto(dto.subscriptionInfo) : undefined,
      chatInfo: dto.chatInfo ? ChatInfoRemapper.fromDto(dto.chatInfo) : undefined,
      chatVideoCallInfo: dto.chatVideoCallInfo ? ChatVideoCallInfoRemapper.fromDto(dto.chatVideoCallInfo) : undefined,
      premiumPackageInfo: dto.premiumPackageInfo ? PremiumPackageInfoRemapper.fromDto(dto.premiumPackageInfo) : undefined,
      roadmapItemInfo: dto.roadmapItemInfo ? RoadmapItemInfoRemapper.fromDto(dto.roadmapItemInfo) : undefined,
      transactionType: dto.transactionType,
      createdDate: dto.createdDate,
      updatedDate: dto.updatedDate,
      expireDate: dto.expireDate,
      name: dto.name,
      description: dto.description,
    };
  }

}
